import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { AssistantUserPromptLog } from '@mews-commander/data-access';
import { IconName } from '@optimus-web/icons';
import { DataTestId, TextKey } from '@mews-commander/enums';
import { Stack, StackItem, Typography } from '@optimus-web/core';
import { lt } from '@mews/framework';
import { createUUID } from '@mews/std';
import { highlight } from '../../utils/highlight';
import { StyledLeftIcon, ThinDivider } from '../styles';
import { AssistantRemoveFavoriteButton } from '../components/AssistantRemoveFavoriteButton';
import { AssistantRemoveRecentButton } from '../components/AssistantRemoveRecentButton';
import { useAssistantRecentSearchPromptQuery } from '../../query/useAssistantRecentSearchPromptQuery';
import { AssistantRecentAndFavoriteListItem } from '../components/AssistantRecentAndFavoriteListItem';
import { AdditionalSearchType } from '../../assistantSearchTypes';
import { FilterStateProps, SearchStateProps } from '../../Assistant';
import { AssistantSearchListItemProps } from './useAssistantSearchResultListMap';

interface RecentAndFavoriteListProps {
    recentSearchTerm: string;
    setSearchState: Dispatch<SetStateAction<SearchStateProps>>;
    setFilterState: Dispatch<SetStateAction<FilterStateProps>>;
    isTabNavigation: boolean;
    selectedIndex: number;
}

const ListTitle = ({
    title,
    hideDivider = false,
}: {
    title: TextKey.Favorite | TextKey.Recent;
    hideDivider: boolean;
}) => (
    <Stack vertical>
        <StackItem>
            <Typography textStyle="bodySmall" variant="tertiary" as="div">
                {lt(title)}
            </Typography>
        </StackItem>
        {!hideDivider && (
            <StackItem>
                <ThinDivider />
            </StackItem>
        )}
    </Stack>
);

export const useRecentAndFavoriteList = ({
    recentSearchTerm,
    setSearchState,
    setFilterState,
    isTabNavigation,
    selectedIndex,
}: RecentAndFavoriteListProps) => {
    const [favorites, setFavorites] = useState<Array<AssistantUserPromptLog>>();
    const [recents, setRecent] = useState<Array<AssistantUserPromptLog>>();
    const { data, isFetching: recentAndFavoriteFetching } = useAssistantRecentSearchPromptQuery();

    const recentAndFavoriteMergedList = useMemo(() => [...(favorites || []), ...(recents || [])], [favorites, recents]);

    useEffect(() => {
        if (!recentAndFavoriteFetching) {
            if (data?.favorites) {
                setFavorites((_) => [...(data.favorites || [])]);
            }
            if (data?.recents) {
                setRecent((_) => [...(data.recents || [])]);
            }
        }
    }, [data?.favorites, data?.recents, recentAndFavoriteFetching]);

    const addRecent = useCallback(
        (prompt: string) => {
            const exist = recentAndFavoriteMergedList?.find((x) => x.prompt === prompt);
            if (exist) {
                return;
            }

            setRecent((prevState) => {
                const newValue: AssistantUserPromptLog = {
                    id: createUUID(),
                    prompt,
                    profileId: '',
                };
                if (prevState && prevState?.length > 0) {
                    const recentsState = [...prevState];
                    /** This object is created to handle state in the front end  */
                    recentsState.unshift(newValue);
                    return recentsState;
                }
                return [newValue];
            });
        },
        [recentAndFavoriteMergedList]
    );

    const favoriteListItems: Array<AssistantSearchListItemProps> = useMemo(
        () =>
            favorites?.map((favorite) => ({
                itemId: favorite.id,
                'data-test-id': DataTestId.AssistantFavoriteSearchCard,
                primaryText: highlight(favorite.prompt, recentSearchTerm),
                leftIcon: <StyledLeftIcon size="small" value={IconName.Stars} />,
                value: favorite.prompt,
                rightItem: <AssistantRemoveFavoriteButton favorite={favorite} />,
            })) ?? [],
        [favorites, recentSearchTerm]
    );
    const recentListItems: Array<AssistantSearchListItemProps> = useMemo(
        () =>
            recents?.map((recent) => ({
                itemId: recent.id,
                'data-test-id': DataTestId.AssistantRecentSearchCard,
                primaryText: highlight(recent.prompt, recentSearchTerm),
                leftIcon: <StyledLeftIcon size="small" value={IconName.Time} />,
                value: recent.prompt,
                rightItem: <AssistantRemoveRecentButton recent={recent} />,
            })) ?? [],
        [recentSearchTerm, recents]
    );

    const recentAndFavoriteListComponent = useMemo(() => {
        let initialIndex = 0;

        const handleClick = (searchTerm: string) => {
            setSearchState((prev) => ({
                ...prev,
                searchTerm,
                recentSearchTerm: searchTerm,
            }));
            setFilterState((prev) => ({
                ...prev,
                selectedSearchType: AdditionalSearchType.All,
            }));
        };

        const generateList = (list: Array<AssistantSearchListItemProps>) =>
            list.map((props) => {
                initialIndex++;
                const isSelected = !isTabNavigation && selectedIndex === initialIndex;
                return (
                    <AssistantRecentAndFavoriteListItem
                        key={props.itemId}
                        assistantSearchListItemProps={props}
                        handleClick={handleClick}
                        recentAndFavoriteMergedListLength={recentAndFavoriteMergedList.length}
                        focused={isSelected}
                    />
                );
            });

        if (recentAndFavoriteMergedList.length < 1) {
            return null;
        }

        return (
            <Stack spacing="100" vertical>
                <Stack vertical spacing="50" className="stack-list">
                    {favoriteListItems.length > 0 && (
                        <>
                            <ListTitle title={TextKey.Favorite} hideDivider />
                            {generateList(favoriteListItems)}
                        </>
                    )}
                    {favoriteListItems.length > 0 && recentListItems.length > 0 && <ThinDivider />}
                    {recentListItems.length > 0 && (
                        <>
                            <ListTitle title={TextKey.Recent} hideDivider />
                            {generateList(recentListItems)}
                        </>
                    )}
                </Stack>
            </Stack>
        );
    }, [
        favoriteListItems,
        isTabNavigation,
        recentAndFavoriteMergedList.length,
        recentListItems,
        selectedIndex,
        setFilterState,
        setSearchState,
    ]);

    return useMemo(
        () => ({
            recentAndFavoriteListComponent,
            recentAndFavoriteFetching,
            favoriteListItems,
            recentAndFavoriteMergedList,
            addRecent,
        }),
        [
            recentAndFavoriteListComponent,
            recentAndFavoriteFetching,
            favoriteListItems,
            recentAndFavoriteMergedList,
            addRecent,
        ]
    );
};
